import type { HighlightRanges } from '@nozbe/microfuzz';
import { Highlight } from '@nozbe/microfuzz/react';
import { isEmpty } from 'rambda';
import { SafeImage } from '@/components/common/safe-image';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  avatarImageUrl: string;
  title: string;
  description?: string;
  highlight?: string | HighlightRanges | null | undefined;
  highlightField?: 'title' | 'description' | 'suffix';
  suffix?: React.ReactNode;
  onClick?: () => void;
}

export default function SearchListItem({
  className,
  avatarImageUrl,
  title,
  titleClassName,
  description,
  descriptionClassName,
  suffix,
  highlight,
  highlightField = 'title',
  onClick,
}: Props) {
  return (
    <div
      className={cn('flex w-full items-center px-5 py-2', className)}
      onClick={onClick}>
      {avatarImageUrl && (
        <SafeImage
          src={avatarImageUrl}
          className='size-10 shrink-0 rounded-full bg-cover'
          size={[40, 40]}
        />
      )}
      <div className='mx-4 flex flex-auto grow flex-col gap-1 overflow-hidden'>
        <h6 className={cn(' text-sm font-semibold', titleClassName)}>
          {highlightField === 'title' ? (
            <HighlightKeyword
              text={title}
              highlight={highlight}
            />
          ) : (
            title
          )}
        </h6>
        {description && (
          <p className={cn('truncate text-xs text-base-content/50', descriptionClassName)}>
            {highlightField === 'description' ? (
              <HighlightKeyword
                text={description}
                highlight={highlight}
              />
            ) : (
              description
            )}
          </p>
        )}
      </div>
      {suffix && typeof suffix === 'string' ? (
        <div className='whitespace-nowrap text-sm'>
          {highlightField === 'suffix' ? (
            <HighlightKeyword
              text={suffix}
              highlight={highlight}
            />
          ) : (
            suffix
          )}
        </div>
      ) : (
        suffix
      )}
    </div>
  );
}

export function HighlightKeyword({ text, highlight }: Pick<Props, 'highlight'> & { text: string }) {
  if (!highlight || isEmpty(highlight)) return text;

  if (typeof highlight === 'string') {
    const escapedKeyword = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedKeyword, 'ig');

    if (text.match(regex) === null) return text;
    const { index = 0 } = regex.exec(text) ?? {};
    const ranges: HighlightRanges = [[index, index + highlight.length - 1]];
    return (
      <Highlight
        style={{}}
        className='text-primary'
        text={text}
        ranges={ranges}
      />
    );
  }

  if (Array.isArray(highlight)) {
    return (
      <Highlight
        style={{}}
        className='text-primary'
        text={text}
        ranges={highlight}
      />
    );
  }
}
